import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, Shield, Globe, Play } from "lucide-react";
import { useSecureAuthContext } from "@/contexts/SecureAuthContext";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import DemoModal from "@/components/DemoModal";
import { useSiteConfigValue } from "@/hooks/useSiteConfigValue";
import superLaunchBadge from "@/assets/badges/superlaunch-badge.png";
import startupFameBadge from "@/assets/badges/startupfame-badge.webp";
import dofollowToolsBadge from "@/assets/badges/dofollow-tools-badge.svg";
const EnterpriseHero = () => {
  const {
    user
  } = useSecureAuthContext();
  const navigate = useNavigate();
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  // Get demo video URL from site configuration
  const {
    value: demoVideoUrl
  } = useSiteConfigValue('demo_video_url', 'https://www.youtube.com/watch?v=dQw4w9WgXcQ');
  const {
    value: demoVideoTitle
  } = useSiteConfigValue('demo_video_title', 'Product Demo');
  const handleStartFreeTrial = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/auth');
    }
  };
  const handleViewDemo = () => {
    setDemoModalOpen(true);
  };
  return <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Animated background grid */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:12px_12px] xs:bg-[size:16px_16px] sm:bg-[size:20px_20px] md:bg-[size:24px_24px]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
      </div>

      {/* Static orbs - enhanced mobile sizing */}
      <div className="absolute top-1/4 left-1/4 w-16 h-16 xs:w-24 xs:h-24 sm:w-32 sm:h-32 lg:w-64 lg:h-64 bg-blue-500/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-1/4 w-24 h-24 xs:w-32 xs:h-32 sm:w-48 sm:h-48 lg:w-96 lg:h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 w-8 h-8 xs:w-12 xs:h-12 sm:w-16 sm:h-16 lg:w-32 lg:h-32 bg-cyan-500/30 rounded-full blur-2xl"></div>

      <div className="container mx-auto px-3 xs:px-4 sm:px-6 md:px-6 lg:px-8 relative z-10 max-w-7xl">
        <div className="text-center my-0 py-12 xs:py-16 sm:py-20 md:py-[80px]">
          {/* Badge - Mobile optimized */}
          <div className="flex justify-center mb-6 xs:mb-8 sm:mb-10 md:mb-8">
            <div className="inline-flex items-center px-3 py-2 xs:px-4 xs:py-2 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r from-blue-600/30 to-purple-600/30 backdrop-blur-sm border border-white/20 shadow-lg">
              <Zap className="w-4 h-4 xs:w-4 xs:h-4 text-blue-400 mr-2 flex-shrink-0" />
              <span className="text-sm xs:text-sm font-medium text-gray-200">Enterprise AI Image Generation</span>
            </div>
          </div>

          {/* Main heading - Mobile-first typography */}
          <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold mb-4 xs:mb-6 sm:mb-8 md:mb-6 leading-tight px-1 xs:px-2">
            <span className="text-white drop-shadow-lg block mb-2 xs:mb-3 sm:mb-4 leading-tight">From Ideas to Images, Instantly?</span>
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent block leading-tight">
              Scale Your Vision with AI
            </span>
          </h1>

          {/* Subtitle - Mobile optimized readability */}
          <p className="text-sm xs:text-base sm:text-lg md:text-lg lg:text-xl text-gray-300 mb-6 xs:mb-8 sm:mb-10 lg:mb-10 max-w-sm xs:max-w-lg sm:max-w-2xl lg:max-w-4xl mx-auto leading-relaxed px-2 xs:px-4 sm:px-0">
            Generate thousands of high-quality images instantly with our enterprise-grade AI platform. 
            Built for teams, agencies, and businesses that demand scale, security, and reliability.
          </p>

          {/* Feature highlights - Enhanced mobile visibility */}
          <div className="mb-6 xs:mb-8 sm:mb-10 lg:mb-10 px-2 xs:px-4">
            <div className="flex flex-col gap-2 xs:gap-3 sm:gap-3 max-w-4xl mx-auto sm:flex-row sm:justify-center sm:items-center">
              <div className="flex items-center bg-white/25 backdrop-blur-md rounded-xl px-3 py-3 xs:px-4 xs:py-3 border border-white/30 shadow-xl justify-center touch-manipulation">
                <Zap className="w-4 h-4 xs:w-5 xs:h-5 text-blue-400 mr-2 flex-shrink-0" />
                <span className="text-xs xs:text-sm font-semibold text-white whitespace-nowrap">1000+ Images/hr</span>
              </div>
              <div className="flex items-center bg-white/25 backdrop-blur-md rounded-xl px-3 py-3 xs:px-4 xs:py-3 border border-white/30 shadow-xl justify-center touch-manipulation">
                <Shield className="w-4 h-4 xs:w-5 xs:h-5 text-green-400 mr-2 flex-shrink-0" />
                <span className="text-xs xs:text-sm font-semibold text-white whitespace-nowrap">Data Security</span>
              </div>
              <div className="flex items-center bg-white/25 backdrop-blur-md rounded-xl px-3 py-3 xs:px-4 xs:py-3 border border-white/30 shadow-xl justify-center touch-manipulation">
                <Globe className="w-4 h-4 xs:w-5 xs:h-5 text-purple-400 mr-2 flex-shrink-0" />
                <span className="text-xs xs:text-sm font-semibold text-white whitespace-nowrap">Global Infrastructure</span>
              </div>
            </div>
          </div>

          {/* CTA buttons - Single row layout without glowing effect */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center px-2 xs:px-4 sm:px-0 mb-8 xs:mb-12 sm:mb-16 md:mb-12">
            <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-5 py-3 xs:px-6 xs:py-4 sm:px-6 sm:py-3 text-sm xs:text-base sm:text-base font-semibold w-full sm:w-auto h-12 xs:h-14 sm:h-auto shadow-2xl touch-manipulation active:scale-95 transition-transform" onClick={() => navigate('/try-generator')} noGlow>
              Try Guest Mode
              <ArrowRight className="ml-2 xs:ml-2 w-4 h-4 xs:w-4 xs:h-4 sm:w-4 sm:h-4" />
            </Button>
            <Button size="lg" variant="outline" className="bg-black/30 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm px-5 py-3 xs:px-6 xs:py-4 sm:px-6 sm:py-3 text-sm xs:text-base sm:text-base font-semibold w-full sm:w-auto h-12 xs:h-14 sm:h-auto shadow-xl touch-manipulation active:scale-95 transition-transform" onClick={handleViewDemo} noGlow>
              <Play className="ml-2 xs:ml-2 w-4 h-4 xs:w-4 xs:h-4 sm:w-4 sm:h-4 mr-2" />
              View Demo
            </Button>
          </div>

          {/* Trust indicators - Mobile optimized */}
          <div className="text-center px-2 xs:px-4 sm:px-0">
            <p className="text-gray-400 text-xs xs:text-xs sm:text-xs mb-3 xs:mb-4 sm:mb-4">Trusted by leading companies worldwide</p>
            <div className="flex flex-wrap justify-center items-center gap-3 xs:gap-4 sm:gap-4 lg:gap-6 opacity-60 mb-6 xs:mb-8 sm:mb-10">
              <div className="text-gray-500 font-semibold text-xs xs:text-sm sm:text-sm">TechCorp</div>
              <div className="text-gray-500 font-semibold text-xs xs:text-sm sm:text-sm">InnovateLab</div>
              <div className="text-gray-500 font-semibold text-xs xs:text-sm sm:text-sm">CreativeStudio</div>
              <div className="text-gray-500 font-semibold text-xs xs:text-sm sm:text-sm">GlobalBrand</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Demo Modal */}
      <DemoModal open={demoModalOpen} onOpenChange={setDemoModalOpen} videoUrl={demoVideoUrl} title={demoVideoTitle} />
    </section>;
};
export default EnterpriseHero;